import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from "react";
import SwiperCarousel from "../../components/common/SwiperCarousel";
import { IoPlay, IoSearch, IoGrid, IoList, IoBookmarkOutline, IoBookmark, IoThumbsUp, IoChevronForward, IoDocumentText, IoBook, IoNewspaper, IoVideocam, IoClose, IoChevronBack, IoHeart, IoChatbubbleOutline } from "react-icons/io5";
import { HiOutlineChevronDown } from "react-icons/hi";
import Modal from '../../components/common/ModalLogin';
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import coursesService from "../../services/CoursesService";
import { getVideos, getVideoById } from "../../services/VideosService";
import documentsService from "../../services/DocumentsService";
import SplashScreen from "../../components/SplashScreen/SplashScreen";
import bannerService from "../../services/BannerService";
import Navbar from "../../components/layout/Navbar";
import postsService from "../../services/PostsService";
import MyCourses from "../../components/Curses/MyCourses";
import Cview from "../../components/Curses/Cview";
import WelcomeCourseBanner from "../../components/common/WelcomeCourseBanner";
import { tokenUtils } from "../../utils/tokenUtils";
import { useUsuarioConCedula } from "../../hooks/useUsuarioConCedula";
import { useWelcomeCourseStatus } from "../../hooks/useWelcomeCourseStatus";
import { recordEnrollment, countThisWeekEnrollments } from "../../utils/recentEnrollments";
import analyticsService from "../../services/AnalyticsService";

const SaveIcon = ({ saved, className }) => (
    saved ? <IoBookmark className={className} /> : <IoBookmarkOutline className={className} />
);

const Home = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useContext(AuthContext);
    const { usuarioConCedula } = useUsuarioConCedula();
    const isAuthenticated = Boolean(user && tokenUtils.get());
    const {
        isBlocked: welcomeBlocked,
        courseId: welcomeCourseId,
        courseTitle: welcomeCourseTitle,
    } = useWelcomeCourseStatus();
    
    const [showSplash, setShowSplash] = useState(true);
    const [isFirstVisit, setIsFirstVisit] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isPostModalOpen, setIsPostModalOpen] = useState(false);
    const [selectedPost, setSelectedPost] = useState(null);
    const [postContent, setPostContent] = useState(null);
    const [loadingPost, setLoadingPost] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [activeTab, setActiveTab] = useState("cursos");
    const [viewMode, setViewMode] = useState("grid");
    const [selectedCategory, setSelectedCategory] = useState("todas");
    const [selectedSubCategory, setSelectedSubCategory] = useState(null);
    const [sortBy, setSortBy] = useState("recientes");
    const [isCategoryMenuOpen, setIsCategoryMenuOpen] = useState(false);
    const [expandedCategory, setExpandedCategory] = useState(null);
    const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
    const [isCollectionMenuOpen, setIsCollectionMenuOpen] = useState(false);
    const [selectedCollection, setSelectedCollection] = useState(null);
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [courseSearchTerm, setCourseSearchTerm] = useState("");
    const [enrollAlert, setEnrollAlert] = useState({ open: false, message: "" });
    const [recentCoursesCount, setRecentCoursesCount] = useState(0);
    const [myCoursesRefreshKey, setMyCoursesRefreshKey] = useState(0);
    const [enrollingFromPost, setEnrollingFromPost] = useState(false);

    useEffect(() => {
        analyticsService.trackEvent('landing_view', {
            userId: usuarioConCedula?.id,
            properties: { page: 'home' },
        });
    }, [usuarioConCedula?.id]);

    useEffect(() => {
        const hasVisited = sessionStorage.getItem('hasVisitedHome');
        if (hasVisited) {
            setShowSplash(false);
            setIsFirstVisit(false);
        } else {
            sessionStorage.setItem('hasVisitedHome', 'true');
        }
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get("shared") || params.get("courseId")) {
            setActiveTab("cursos");
        }
    }, [location.search]);

    const handleSplashComplete = useCallback(() => {
        setShowSplash(false);
    }, []);

    // Datos dummy como respaldo
    const dummyCarouselItems = useMemo(() => [
        {
            image: "/images/slide1.jpg",
            title: "Bienvenido a Docentia",
            description: "Tu plataforma de aprendizaje digital",
        },
        {
            image: "/images/slide2.jpg",
            title: "Gestiona tu conocimiento",
            description: "Accede a cursos y herramientas especializadas",
        },
        {
            image: "/images/slide3.jpg",
            title: "Crece profesionalmente",
            description: "Desarrolla nuevas habilidades con nosotros",
        },
    ], []);

    const [carouselItems, setCarouselItems] = useState(dummyCarouselItems);

    // Obtener banners desde el API
    useEffect(() => {
        const fetchBanners = async () => {
            try {
                const banners = await bannerService.getActiveBanners();
                
                if (banners && Array.isArray(banners) && banners.length > 0) {
                    // Respetar el orden indicado en admin (menor = primero)
                    const sortedBanners = [...banners].sort((a, b) => {
                        const ordenA = Number(a?.orden ?? a?.Orden ?? 0);
                        const ordenB = Number(b?.orden ?? b?.Orden ?? 0);
                        if (ordenA !== ordenB) return ordenA - ordenB;
                        return Number(a?.id ?? a?.Id ?? 0) - Number(b?.id ?? b?.Id ?? 0);
                    });

                    // Transformar los banners al formato esperado por el carrusel
                    const transformedBanners = sortedBanners.map((banner) => {
                        let imageUrl = "/img/default.png";
                        
                        if (banner.imagePath) {
                            // Si ya es una URL completa, usarla directamente
                            if (banner.imagePath.startsWith('http://') || banner.imagePath.startsWith('https://')) {
                                imageUrl = banner.imagePath;
                            } else {
                                // Construir la URL con REACT_APP_LOCATION
                                const cleanPath = banner.imagePath.startsWith('/') ? banner.imagePath : `/${banner.imagePath}`;
                                imageUrl = `${process.env.REACT_APP_LOCATION || ''}${cleanPath}`;
                            }
                        }
                        
                        return {
                            id: banner.id ?? banner.Id,
                            image: imageUrl,
                            title: banner.text || banner.Text || 'Banner',
                            description: banner.text || banner.Text || '',
                            orden: Number(banner.orden ?? banner.Orden ?? 0),
                            linkedPostId:
                                banner.postId ??
                                banner.PostId ??
                                banner.postID ??
                                banner.publicationId ??
                                banner.PublicationId ??
                                banner.post?.id ??
                                banner.Post?.id ??
                                null,
                            linkedCourseId:
                                banner.courseId ??
                                banner.CourseId ??
                                null,
                            linkedCourseTitle:
                                banner.courseTitle ??
                                banner.CourseTitle ??
                                null,
                        };
                    });
                    
                    setCarouselItems(transformedBanners);
                } else {
                    // Si no hay banners, usar los datos dummy
                    setCarouselItems(dummyCarouselItems);
                }
            } catch (err) {
                console.error('Error al cargar banners desde el API:', err);
                // En caso de error, usar los datos dummy como respaldo
                setCarouselItems(dummyCarouselItems);
            }
        };

        fetchBanners();
    }, [dummyCarouselItems]);

    // Datos dummy para visualización - diferentes categorías
    const dummyPosts = useMemo(() => [
        // Videos
        {
            id: 1,
            title: "GENERALIDADES DERMATOLÓGICAS 2025",
            description: "Nuevo curso. Aprende a cuidar la piel con ciencia y práctica",
            thumbnail: "/img/Curse1.jpg",
            category: "video",
            teacherName: "Dr. Jose Luis De la Hoz",
            teacherRole: "Médico de producto",
            views: 150,
            likes: 85,
            createdAt: new Date().toISOString(),
            videoUrl: "/videos/muestra.mp4",
            path: "/videos/muestra.mp4",
        },
        {
            id: 2,
            title: "Porque el clima cambia Pero el cuidado sigue...",
            description: "Conoce nuestra línea de productos para Pico Respiratorio",
            thumbnail: "/img/Curse2.jpg",
            category: "video",
            teacherName: "Equipo Megalabs",
            teacherRole: "Línea Respiratoria",
            views: 230,
            likes: 120,
            createdAt: new Date(Date.now() - 86400000).toISOString(),
        },
        // Ebooks
        {
            id: 3,
            title: "Guía Completa de Farmacología Clínica",
            description: "Ebook completo sobre farmacología y sus aplicaciones en la práctica médica",
            thumbnail: "/img/Curse3.jpg",
            category: "ebook",
            teacherName: "Dr. Carlos Ramírez",
            teacherRole: "Farmacólogo Clínico",
            views: 180,
            likes: 95,
            createdAt: new Date(Date.now() - 172800000).toISOString(),
            fileUrl: "https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf",
            path: "https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf",
        },
        {
            id: 4,
            title: "Manual de Dermatología Moderna",
            description: "Ebook actualizado con las últimas técnicas en cuidado de la piel",
            thumbnail: "/img/Curse4.jpg",
            category: "ebook",
            teacherName: "Dr. Ana Martínez",
            teacherRole: "Dermatóloga Certificada",
            views: 320,
            likes: 200,
            createdAt: new Date(Date.now() - 259200000).toISOString(),
        },
        // Más videos
        {
            id: 9,
            title: "UMBRELLA GEL 2025",
            description: "Nuevo Curso. Umbrella Gel Indicado para piel grasa o mixta",
            thumbnail: "/img/Curse9.jpg",
            category: "video",
            teacherName: "Dr. Jose Luis De la Hoz",
            teacherRole: "Médico de producto",
            views: 520,
            likes: 310,
            createdAt: new Date(Date.now() - 691200000).toISOString(),
        },
        {
            id: 10,
            title: "Avances en Medicina Interna",
            description: "Actualización sobre las últimas tendencias en medicina interna",
            thumbnail: "/img/Curse10.jpg",
            category: "video",
            teacherName: "Dr. María González",
            teacherRole: "Especialista en Medicina Interna",
            views: 340,
            likes: 195,
            createdAt: new Date(Date.now() - 777600000).toISOString(),
        },
        // Más ebooks
        {
            id: 11,
            title: "Atlas de Anatomía Clínica",
            description: "Ebook interactivo con ilustraciones detalladas de anatomía humana",
            thumbnail: "/img/Curse11.jpg",
            category: "ebook",
            teacherName: "Dr. Laura Fernández",
            teacherRole: "Anatomista",
            views: 410,
            likes: 245,
            createdAt: new Date(Date.now() - 864000000).toISOString(),
        },
    ], []);

    // Check if user came from a protected route and save redirect URL
    useEffect(() => {
        const existingRedirect = localStorage.getItem('redirectAfterLogin');
        if (existingRedirect) {
            return;
        }

        const referrer = document.referrer;
        const currentOrigin = window.location.origin;
        
        if (referrer && referrer.startsWith(currentOrigin) && referrer.includes('/courses/')) {
            const referrerUrl = new URL(referrer);
            const redirectPath = referrerUrl.pathname + referrerUrl.search;
            localStorage.setItem('redirectAfterLogin', redirectPath);
        }
    }, []);


    // Función para transformar posts del API al formato esperado
    const transformPost = (post) => {
        // Normalizar categoría a minúsculas
        const normalizedCategory = post.category?.toLowerCase() || 'video';
        
        const rawThumbnail = post.thumbnail || post.thumbnailPath || '';

        // Construir URL completa para thumbnail
        let thumbnailUrl = rawThumbnail || "/img/default.png";
        if (rawThumbnail && !rawThumbnail.startsWith('http')) {
            const cleanPath = rawThumbnail.startsWith('/') ? rawThumbnail : `/${rawThumbnail}`;
            thumbnailUrl = `${process.env.REACT_APP_LOCATION || ''}${cleanPath}`;
        }
        
        // Construir URL completa para video
        let videoUrl = post.videoUrl || post.path;
        if (videoUrl && !videoUrl.startsWith('http')) {
            const cleanPath = videoUrl.startsWith('/') ? videoUrl : `/${videoUrl}`;
            videoUrl = `${process.env.REACT_APP_LOCATION || ''}${cleanPath}`;
        }
        
        // Construir URL completa para archivos (ebooks/documentos)
        let fileUrl = post.fileUrl || post.path;
        if (fileUrl && !fileUrl.startsWith('http')) {
            const cleanPath = fileUrl.startsWith('/') ? fileUrl : `/${fileUrl}`;
            fileUrl = `${process.env.REACT_APP_LOCATION || ''}${cleanPath}`;
        }
        
        return {
            id: post.id,
            title: post.title || '',
            description: post.description || '',
            thumbnail: thumbnailUrl,
            category: normalizedCategory,
            teacherName: post.teacherName || post.teacher || '',
            teacherRole: post.teacherRole || '',
            views: post.views ?? post.Views ?? post.viewCount ?? post.totalViews ?? 0,
            likes: post.likes ?? post.Likes ?? post.likeCount ?? post.totalLikes ?? 0,
            isInCollection: Boolean(
                post.isInUserCollection ?? post.isInCollection ?? post.inCollection ?? post.isSaved ?? post.saved ?? post.IsInCollection ?? false
            ),
            isLiked: Boolean(
                post.isLikedByUser ?? post.isLiked ?? post.liked ?? post.hasLike ?? post.HasLike ?? post.IsLiked ?? false
            ),
            createdAt: post.createdAt || new Date().toISOString(),
            videoUrl: videoUrl,
            path: videoUrl || fileUrl,
            fileUrl: fileUrl,
            labels: post.labels || []
        };
    };

    const normalizePostsData = (data) => {
        if (Array.isArray(data)) {
            return data;
        }
        if (Array.isArray(data?.data)) {
            return data.data;
        }
        if (Array.isArray(data?.posts)) {
            return data.posts;
        }
        return [];
    };

    // Fetch posts (videos, ebooks, documentos, noticias)
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                const categoryParam = selectedCategory === 'video'
                    ? 'Video'
                    : selectedCategory === 'ebook'
                        ? 'E-book'
                        : null;
                const sortParam = sortBy === 'recientes'
                    ? 'recent'
                    : sortBy === 'antiguos'
                        ? 'oldest'
                        : sortBy === 'mas-vistos'
                            ? 'views'
                            : sortBy === 'mas-likes'
                                ? 'likes'
                                : null;
                const postsParams = {
                    ...(categoryParam ? { category: categoryParam } : {}),
                    ...(sortParam ? { sortBy: sortParam } : {}),
                    ...(isAuthenticated && selectedCollection === 'guardados' ? { onlyCollection: true } : {}),
                    ...(isAuthenticated && selectedCollection === 'me-gusta' ? { onlyLiked: true } : {}),
                };
                const isPersonalCollection = selectedCollection === 'guardados' || selectedCollection === 'me-gusta';
                const publicPostsParams = {};
                
                if (isAuthenticated) {
                    // Si el usuario está logueado, obtener posts desde el API
                    try {
                        const postsData = normalizePostsData(await postsService.getPosts(postsParams));
                        if (postsData.length > 0 || isPersonalCollection) {
                            // Una colección personal vacía debe verse vacía, no con contenido de ejemplo
                            const transformedPosts = postsData.map(transformPost);
                            setPosts(transformedPosts);
                        } else {
                            // Si no hay posts, usar datos dummy como respaldo
                            setPosts(dummyPosts);
                        }
                    } catch (postsError) {
                        console.error("Error al obtener posts desde el API:", postsError);
                        if (isPersonalCollection) {
                            setPosts([]);
                            return;
                        }
                        // En caso de error, intentar con videos y cursos como respaldo
                        const [videosData, coursesData] = await Promise.all([
                            getVideos().catch(() => []),
                            coursesService.getCourses({}, navigate).catch(() => [])
                        ]);
                        
                        const allPosts = [
                            ...videosData.map(v => ({ ...v, category: v.category || 'video' })),
                            ...coursesData.map(c => ({ ...c, category: c.category || 'course' }))
                        ];
                        
                        setPosts(allPosts.length > 0 ? allPosts : dummyPosts);
                    }
                } else {
                    // Si el usuario no está logueado, obtener posts públicos desde el API
                    try {
                        const publicPostsData = normalizePostsData(await postsService.getPublicPosts(publicPostsParams));
                        if (publicPostsData.length > 0) {
                            const transformedPosts = publicPostsData.map(transformPost);
                            setPosts(transformedPosts);
                        } else {
                            setPosts(dummyPosts);
                        }
                    } catch (publicPostsError) {
                        console.error("Error al obtener posts públicos desde el API:", publicPostsError);
                        setPosts(dummyPosts);
                    }
                }
            } catch (error) {
                console.error("Error fetching data:", error);
                // En caso de error, usar datos dummy
                setPosts(dummyPosts);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [isAuthenticated, navigate, dummyPosts, selectedCategory, selectedCollection, sortBy]);


    // Eliminado: useEffect que redirigía después del login
    // Ahora solo se actualiza el header sin recargar la página

    const openModal = useCallback(() => {
        setIsModalOpen(true);
    }, []);

    const closeModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    const loadRecentCoursesCount = useCallback(async () => {
        if (!usuarioConCedula?.id) {
            setRecentCoursesCount(0);
            return;
        }

        try {
            const userCourses = await coursesService.getUserCourses(usuarioConCedula.id, navigate);
            setRecentCoursesCount(countThisWeekEnrollments(userCourses || []));
        } catch {
            setRecentCoursesCount(countThisWeekEnrollments([]));
        }
    }, [usuarioConCedula?.id, navigate]);

    useEffect(() => {
        loadRecentCoursesCount();
    }, [loadRecentCoursesCount, myCoursesRefreshKey]);

    useEffect(() => {
        if (!enrollAlert.open) return undefined;
        const timer = setTimeout(() => setEnrollAlert({ open: false, message: "" }), 5000);
        return () => clearTimeout(timer);
    }, [enrollAlert.open]);

    const handleCourseEnrollSuccess = useCallback((courseId, courseTitle) => {
        recordEnrollment(courseId);
        setEnrollAlert({
            open: true,
            message: `Te has inscrito correctamente al curso "${courseTitle}".`,
        });
        setActiveTab("mis-cursos");
        setMyCoursesRefreshKey((key) => key + 1);
    }, []);

    const getCollectionFlag = useCallback((post) => (
        Boolean(post?.isInCollection ?? post?.inCollection ?? post?.isSaved ?? post?.saved ?? post?.IsInCollection ?? false)
    ), []);

    const getLikeFlag = useCallback((post) => (
        Boolean(post?.isLiked ?? post?.liked ?? post?.hasLike ?? post?.HasLike ?? post?.IsLiked ?? false)
    ), []);

    // Evita que un doble clic dispare dos peticiones opuestas sobre la misma publicación
    const pendingPostActionsRef = useRef(new Set());

    const handleToggleCollection = useCallback(async (post, event) => {
        if (event) {
            event.stopPropagation();
        }

        if (!post?.id) {
            return;
        }

        if (!isAuthenticated) {
            openModal();
            return;
        }

        const actionKey = `collection-${post.id}`;
        if (pendingPostActionsRef.current.has(actionKey)) {
            return;
        }
        pendingPostActionsRef.current.add(actionKey);

        const isInCollection = getCollectionFlag(post);

        try {
            if (isInCollection) {
                await postsService.removePostFromCollection(post.id);
            } else {
                await postsService.addPostToCollection(post.id);
            }

            const nextValue = !isInCollection;

            setPosts((prevPosts) => (
                !nextValue && selectedCollection === 'guardados'
                    ? prevPosts.filter((item) => item.id !== post.id)
                    : prevPosts.map((item) => (
                        item.id === post.id ? { ...item, isInCollection: nextValue } : item
                    ))
            ));
            setSelectedPost((prevPost) => (
                prevPost && prevPost.id === post.id ? { ...prevPost, isInCollection: nextValue } : prevPost
            ));
            setPostContent((prevContent) => (
                prevContent && prevContent.id === post.id ? { ...prevContent, isInCollection: nextValue } : prevContent
            ));
        } catch (error) {
            console.error("Error al actualizar colección del post:", error);
        } finally {
            pendingPostActionsRef.current.delete(actionKey);
        }
    }, [getCollectionFlag, openModal, isAuthenticated, selectedCollection]);

    const handleToggleLike = useCallback(async (post, event) => {
        if (event) {
            event.stopPropagation();
        }

        if (!post?.id) {
            return;
        }

        if (!isAuthenticated) {
            openModal();
            return;
        }

        const actionKey = `like-${post.id}`;
        if (pendingPostActionsRef.current.has(actionKey)) {
            return;
        }
        pendingPostActionsRef.current.add(actionKey);

        const isLiked = getLikeFlag(post);

        try {
            let serverLikes = null;
            if (isLiked) {
                await postsService.removePostLike(post.id);
            } else {
                const result = await postsService.addPostLike(post.id);
                serverLikes = typeof result?.likes === 'number' ? result.likes : null;
            }

            const nextValue = !isLiked;
            const nextLikes = serverLikes ?? (post.likes || 0) + (nextValue ? 1 : -1);

            setPosts((prevPosts) => (
                !nextValue && selectedCollection === 'me-gusta'
                    ? prevPosts.filter((item) => item.id !== post.id)
                    : prevPosts.map((item) => (
                        item.id === post.id
                            ? { ...item, isLiked: nextValue, likes: Math.max(0, nextLikes) }
                            : item
                    ))
            ));
            setSelectedPost((prevPost) => (
                prevPost && prevPost.id === post.id
                    ? { ...prevPost, isLiked: nextValue, likes: Math.max(0, nextLikes) }
                    : prevPost
            ));
            setPostContent((prevContent) => (
                prevContent && prevContent.id === post.id
                    ? { ...prevContent, isLiked: nextValue, likes: Math.max(0, nextLikes) }
                    : prevContent
            ));
        } catch (error) {
            console.error("Error al actualizar like del post:", error);
        } finally {
            pendingPostActionsRef.current.delete(actionKey);
        }
    }, [getLikeFlag, openModal, isAuthenticated, selectedCollection]);

    // Abrir modal de post
    const pickLinkedCourseFields = (source = {}) => ({
        courseId: source.courseId ?? source.CourseId ?? null,
        courseTitle: source.courseTitle ?? source.CourseTitle ?? null,
        courseThumbnailPath: source.courseThumbnailPath ?? source.CourseThumbnailPath ?? null,
        isEnrolledInLinkedCourse: Boolean(
            source.isEnrolledInLinkedCourse ?? source.IsEnrolledInLinkedCourse
        ),
    });

    const openPostModal = useCallback(async (post) => {
        let resolvedPost = post;
        setSelectedPost(post);
        setIsPostModalOpen(true);
        setLoadingPost(true);
        setCurrentPage(1);
        
        try {
            if (post?.id) {
                try {
                    const detail = isAuthenticated
                        ? await postsService.getPostById(post.id)
                        : await postsService.getPublicPostById(post.id);
                    resolvedPost = { ...post, ...detail };
                    setSelectedPost(resolvedPost);
                } catch (detailError) {
                    console.error("Error al obtener detalle del post:", detailError);
                }
            }

            if (isAuthenticated) {
                try {
                    await postsService.registerPostView(post.id);
                    setPosts((prevPosts) => prevPosts.map((item) => (
                        item.id === post.id
                            ? { ...item, views: (item.views || 0) + 1 }
                            : item
                    )));
                    setSelectedPost((prevPost) => (
                        prevPost && prevPost.id === post.id
                            ? { ...prevPost, views: (prevPost.views || 0) + 1 }
                            : prevPost
                    ));
                } catch (viewError) {
                    console.error("Error al registrar vista del post:", viewError);
                }
            }

            const courseFields = pickLinkedCourseFields(resolvedPost);
            const category = (resolvedPost.category || '').toString().toLowerCase();

            // Obtener contenido completo según el tipo
            if (category === 'video') {
                try {
                    // Intentar obtener datos del API solo si el usuario está logueado
                    if (isAuthenticated) {
                        const videoData = await getVideoById(resolvedPost.id);
                        setPostContent({
                            ...videoData,
                            type: 'video',
                            videoUrl: videoData.videoUrl || videoData.path || videoData.videoPath || resolvedPost.videoUrl || resolvedPost.path,
                            thumbnailUrl: videoData.thumbnailUrl || videoData.thumbnailPath || resolvedPost.thumbnail,
                            likes: videoData.likes ?? resolvedPost.likes,
                            isLiked: videoData.isLiked ?? videoData.liked ?? resolvedPost.isLiked,
                            views: videoData.views ?? resolvedPost.views,
                            ...courseFields,
                        });
                    } else {
                        // Usar datos del post directamente
                        setPostContent({
                            ...resolvedPost,
                            type: 'video',
                            videoUrl: resolvedPost.videoUrl || resolvedPost.path,
                            thumbnailUrl: resolvedPost.thumbnail,
                            likes: resolvedPost.likes,
                            isLiked: resolvedPost.isLiked,
                            views: resolvedPost.views,
                            ...courseFields,
                        });
                    }
                } catch (error) {
                    console.error("Error loading video:", error);
                    setPostContent({
                        ...resolvedPost,
                        type: 'video',
                        videoUrl: resolvedPost.videoUrl || resolvedPost.path,
                            thumbnailUrl: resolvedPost.thumbnail,
                            likes: resolvedPost.likes,
                            isLiked: resolvedPost.isLiked,
                            views: resolvedPost.views,
                            ...courseFields,
                    });
                }
            } else if (category === 'ebook' || category === 'e-book' || category === 'documento') {
                try {
                    // Intentar obtener datos del API solo si el usuario está logueado
                    if (isAuthenticated) {
                        const docData = await documentsService.getDocumentById(resolvedPost.id);
                        setPostContent({
                            ...docData,
                            type: 'pdf',
                            fileUrl: docData.fileUrl || docData.path || docData.filePath || resolvedPost.fileUrl || resolvedPost.path,
                            likes: docData.likes ?? resolvedPost.likes,
                            isLiked: docData.isLiked ?? docData.liked ?? resolvedPost.isLiked,
                            views: docData.views ?? resolvedPost.views,
                            ...courseFields,
                        });
                        // Simular páginas (en producción vendría del backend)
                        setTotalPages(docData.totalPages || 30);
                    } else {
                        // Usar datos del post directamente
                        setPostContent({
                            ...resolvedPost,
                            type: 'pdf',
                            fileUrl: resolvedPost.fileUrl || resolvedPost.path,
                            likes: resolvedPost.likes,
                            isLiked: resolvedPost.isLiked,
                            views: resolvedPost.views,
                            ...courseFields,
                        });
                        setTotalPages(30);
                    }
                } catch (error) {
                    console.error("Error loading document:", error);
                    setPostContent({
                        ...resolvedPost,
                        type: 'pdf',
                        fileUrl: resolvedPost.fileUrl || resolvedPost.path,
                        likes: resolvedPost.likes,
                        isLiked: resolvedPost.isLiked,
                        views: resolvedPost.views,
                        ...courseFields,
                    });
                    setTotalPages(30);
                }
            } else {
                // Para noticias, usar los datos del post
                setPostContent({
                    ...resolvedPost,
                    type: 'noticia',
                    likes: resolvedPost.likes,
                    isLiked: resolvedPost.isLiked,
                    views: resolvedPost.views,
                    ...courseFields,
                });
            }
        } catch (error) {
            console.error("Error loading post content:", error);
            // En caso de error, usar datos del post
            setPostContent({
                ...resolvedPost,
                type: resolvedPost.category,
                videoUrl: resolvedPost.videoUrl || resolvedPost.path,
                fileUrl: resolvedPost.fileUrl || resolvedPost.path,
                likes: resolvedPost.likes,
                isLiked: resolvedPost.isLiked,
                views: resolvedPost.views,
                ...pickLinkedCourseFields(resolvedPost),
            });
        } finally {
            setLoadingPost(false);
        }
    }, [isAuthenticated]);

    const closePostModal = useCallback(() => {
        setIsPostModalOpen(false);
        setSelectedPost(null);
        setPostContent(null);
        setCurrentPage(1);
        setTotalPages(1);
    }, []);

    const handleEnrollFromPost = useCallback(async () => {
        const courseId = postContent?.courseId || selectedPost?.courseId || selectedPost?.CourseId;
        const courseTitle = postContent?.courseTitle || selectedPost?.courseTitle || selectedPost?.CourseTitle || 'curso';
        const postId = selectedPost?.id || postContent?.id;

        if (!courseId || !postId) return;

        if (!isAuthenticated) {
            openModal();
            return;
        }

        if (welcomeBlocked && String(courseId) !== String(welcomeCourseId)) {
            setEnrollAlert({
                open: true,
                message: 'Debes completar el curso de bienvenida antes de inscribirte en otros cursos.',
            });
            return;
        }

        try {
            setEnrollingFromPost(true);
            const result = await postsService.enrollLinkedCourse(postId);
            const enrolledTitle = stripHtml(result?.courseTitle || courseTitle) || 'curso';
            const enrolledId = result?.courseId || courseId;

            setPostContent((prev) => prev ? { ...prev, isEnrolledInLinkedCourse: true } : prev);
            setSelectedPost((prev) => prev ? { ...prev, isEnrolledInLinkedCourse: true } : prev);
            recordEnrollment(enrolledId);
            setMyCoursesRefreshKey((k) => k + 1);
            setEnrollAlert({
                open: true,
                message: result?.alreadyEnrolled
                    ? `Ya estabas inscrito en "${enrolledTitle}".`
                    : `Te has inscrito correctamente al curso "${enrolledTitle}".`,
            });

            closePostModal();
            navigate(`/courses/Introcourse/${enrolledId}`, { state: { objeto: enrolledId } });
        } catch (error) {
            const message =
                error?.response?.data?.message
                || error?.response?.data
                || 'No se pudo completar la inscripción al curso.';
            setEnrollAlert({
                open: true,
                message: typeof message === 'string' ? message : 'No se pudo completar la inscripción al curso.',
            });
        } finally {
            setEnrollingFromPost(false);
        }
    }, [
        postContent,
        selectedPost,
        isAuthenticated,
        welcomeBlocked,
        welcomeCourseId,
        openModal,
        closePostModal,
        navigate,
    ]);

    const handleGoToLinkedCourse = useCallback(() => {
        const courseId = postContent?.courseId || selectedPost?.courseId || selectedPost?.CourseId;
        if (!courseId) return;
        if (!isAuthenticated) {
            openModal();
            return;
        }
        closePostModal();
        navigate(`/courses/Introcourse/${courseId}`, { state: { objeto: courseId } });
    }, [postContent, selectedPost, isAuthenticated, openModal, closePostModal, navigate]);

    const stripHtml = (html) => {
        if (!html) return '';
        const div = document.createElement('div');
        div.innerHTML = html;
        return div.textContent || div.innerText || '';
    };

    const formatDate = (dateString) => {
        if (!dateString) return "Hoy";
        const date = new Date(dateString);
        const today = new Date();
        const diffTime = Math.abs(today - date);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays === 0) return "Hoy";
        if (diffDays === 1) return "Ayer";
        if (diffDays < 7) return `Hace ${diffDays} días`;
        return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
    };

    const formatTime = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    };

    // Función de ordenamiento
    const sortPosts = useCallback((items, sortOption) => {
        const sorted = [...items];
        
        switch (sortOption) {
            case 'recientes':
                return sorted.sort((a, b) => {
                    const dateA = new Date(a.createdAt || a.createdDate || 0);
                    const dateB = new Date(b.createdAt || b.createdDate || 0);
                    return dateB - dateA; // Más recientes primero
                });
            case 'antiguos':
                return sorted.sort((a, b) => {
                    const dateA = new Date(a.createdAt || a.createdDate || 0);
                    const dateB = new Date(b.createdAt || b.createdDate || 0);
                    return dateA - dateB; // Más antiguos primero
                });
            case 'mas-vistos':
                return sorted.sort((a, b) => {
                    const viewsA = a.views || 0;
                    const viewsB = b.views || 0;
                    return viewsB - viewsA; // Más vistos primero
                });
            case 'menos-vistos':
                return sorted.sort((a, b) => {
                    const viewsA = a.views || 0;
                    const viewsB = b.views || 0;
                    return viewsA - viewsB; // Menos vistos primero
                });
            case 'mas-likes':
                return sorted.sort((a, b) => {
                    const likesA = a.likes || 0;
                    const likesB = b.likes || 0;
                    return likesB - likesA; // Más likes primero
                });
            case 'a-z':
                return sorted.sort((a, b) => {
                    const titleA = stripHtml(a.title || '').toLowerCase();
                    const titleB = stripHtml(b.title || '').toLowerCase();
                    return titleA.localeCompare(titleB);
                });
            case 'z-a':
                return sorted.sort((a, b) => {
                    const titleA = stripHtml(a.title || '').toLowerCase();
                    const titleB = stripHtml(b.title || '').toLowerCase();
                    return titleB.localeCompare(titleA);
                });
            default:
                return sorted;
        }
    }, []);

    // Agrupar posts por categoría con filtros y ordenamiento
    const postsByCategory = useMemo(() => {
        const categories = selectedCategory === "todas" 
            ? ['video', 'ebook', 'documento', 'noticia']
            : [selectedCategory];
        const grouped = {};
        
        categories.forEach(category => {
            let categoryPosts = posts.filter(item => {
                // Filtro por categoría
                if (item.category !== category) {
                    return false;
                }
                
                // Filtro por búsqueda
                if (searchTerm) {
                    const searchLower = searchTerm.toLowerCase();
                    const title = stripHtml(item.title || '').toLowerCase();
                    const description = stripHtml(item.description || '').toLowerCase();
                    if (!title.includes(searchLower) && !description.includes(searchLower)) {
                        return false;
                    }
                }
                return true;
            });
            
            // Aplicar ordenamiento
            categoryPosts = sortPosts(categoryPosts, sortBy);
            
            if (categoryPosts.length > 0) {
                grouped[category] = categoryPosts;
            }
        });
        
        return grouped;
    }, [posts, searchTerm, selectedCategory, sortBy, sortPosts]);


    // Función para obtener el icono según la categoría
    const getCategoryIcon = (category) => {
        switch (category) {
            case 'video':
                return <IoVideocam className="text-lg" />;
            case 'ebook':
                return <IoBook className="text-lg" />;
            case 'documento':
                return <IoDocumentText className="text-lg" />;
            case 'noticia':
                return <IoNewspaper className="text-lg" />;
            default:
                return <IoPlay className="text-lg" />;
        }
    };

    // Función para obtener el nombre de la categoría
    const getCategoryName = (category) => {
        switch (category) {
            case 'video':
                return 'Videos';
            case 'ebook':
                return 'Ebooks';
            case 'documento':
                return 'Documentos';
            case 'noticia':
                return 'Noticias';
            case 'todas':
                return 'Todas';
            default:
                return 'Contenido';
        }
    };

    // Cerrar menús al hacer clic fuera
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.relative')) {
                setIsCategoryMenuOpen(false);
                setIsSortMenuOpen(false);
                setIsCollectionMenuOpen(false);
            }
        };

        if (isCategoryMenuOpen || isSortMenuOpen || isCollectionMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [isCategoryMenuOpen, isSortMenuOpen, isCollectionMenuOpen]);

    // Función para obtener el color de la categoría
    const getCategoryColor = (category) => {
        switch (category) {
            case 'video':
                return 'bg-red-100 text-red-700';
            case 'ebook':
                return 'bg-blue-100 text-blue-700';
            case 'documento':
                return 'bg-yellow-100 text-yellow-700';
            case 'noticia':
                return 'bg-green-100 text-green-700';
            default:
                return 'bg-gray-100 text-gray-700';
        }
    };

    const scrollContainer = (direction, categoryId) => {
        const container = document.getElementById(`scroll-${categoryId}`);
        if (container) {
            const scrollAmount = 400;
            container.scrollBy({
                left: direction === 'right' ? scrollAmount : -scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    // Componente de carrusel por categoría
    const CategoryCarousel = ({ category, items, categoryName, categoryColor, categoryIcon, viewMode }) => {
        const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

        useEffect(() => {
            const handleResize = () => {
                setIsDesktop(window.innerWidth >= 1024);
            };
            window.addEventListener('resize', handleResize);
            return () => window.removeEventListener('resize', handleResize);
        }, []);

        if (!items || items.length === 0) return null;

        const renderCard = (item, index) => {
            const thumbnail = item.thumbnail || item.image || "/img/default.png";
            const title = stripHtml(item.title || 'Sin título');
            const description = stripHtml(item.description || '');
            const date = formatDate(item.createdAt || item.createdDate);
            const time = formatTime(item.createdAt || item.createdDate);

            if (viewMode === "list") {
                // Vista de lista - Card pequeña para sidebar (desktop) o completa (móvil)
                return (
                    <div
                        key={item.id || index}
                        className="flex flex-col gap-3 p-3 mb-4 bg-white rounded-xl shadow-md transition-all duration-300 cursor-pointer sm:flex-row sm:gap-3 sm:p-3 hover:shadow-lg"
                        onClick={() => openPostModal(item)}
                    >
                        {/* Thumbnail en lista — imagen completa visible dentro del marco */}
                        <div className="flex overflow-hidden flex-shrink-0 justify-center items-center w-full bg-gray-200 rounded-lg aspect-video sm:w-32">
                            <img
                                src={thumbnail}
                                alt={title}
                                className="object-contain max-w-full max-h-full w-full h-full"
                                onError={(e) => {
                                    e.target.src = "/img/default.png";
                                }}
                            />
                        </div>

                        {/* Contenido en lista */}
                        <div className="flex-1 min-w-0">
                            <div className="flex flex-col gap-2 mb-2 sm:flex-row sm:justify-between sm:items-start">
                                <div className="flex-1">
                                    <h3 className="mb-1 text-sm font-semibold text-gray-800 sm:text-sm line-clamp-2">
                                        {title}
                                    </h3>
                                    {item.teacherName && (
                                        <p className="text-xs text-gray-600">
                                            {item.teacherName}
                                        </p>
                                    )}
                                </div>
                                <div className="flex flex-row gap-2 items-start text-xs text-gray-500 sm:flex-col sm:items-end sm:ml-2 sm:gap-0">
                                    <span>{date}</span>
                                    <span>{time}</span>
                                </div>
                            </div>
                            
                            <p className="mb-2 text-xs text-gray-600 line-clamp-2">
                                {description || 'Sin descripción disponible'}
                            </p>

                            {/* Engagement Metrics */}
                            <div className="flex flex-wrap gap-2 justify-between items-center">
                                <div className="flex flex-wrap gap-2 items-center text-xs text-gray-600">
                                    <span>{item.views ?? 0} Vistas</span>
                                    <button
                                        onClick={(e) => handleToggleLike(item, e)}
                                        className={`flex items-center space-x-1 transition-colors ${
                                            getLikeFlag(item) ? 'text-[#4DABA4]' : 'text-gray-600 hover:text-[#4DABA4]'
                                        }`}
                                    >
                                        <IoThumbsUp className={getLikeFlag(item) ? 'text-[#4DABA4]' : 'text-gray-400'} />
                                        <span>{item.likes ?? 0}</span>
                                    </button>
                                </div>
                                <button 
                                    onClick={(e) => handleToggleCollection(item, e)}
                                    className={`p-1.5 transition-colors ${getCollectionFlag(item) ? 'text-[#4DABA4]' : 'text-gray-400 hover:text-[#4DABA4]'}`}
                                >
                                    <SaveIcon saved={getCollectionFlag(item)} className="text-base" />
                                </button>
                            </div>
                        </div>
                    </div>
                );
            }

            // Vista de grid (carrusel)
            const VideoPreviewCard = ({ item, thumbnail, title, description, date, time }) => {
                const [isHovered, setIsHovered] = useState(false);
                const videoRef = React.useRef(null);
                
                // Obtener URL del video
                const videoUrl = item.videoUrl || item.path || item.videoPath;
                const fullVideoUrl = videoUrl 
                    ? (videoUrl.startsWith('http') ? videoUrl : `${process.env.REACT_APP_LOCATION || ''}${videoUrl}`)
                    : null;

                // Manejar hover para reproducir/pausar video
                useEffect(() => {
                    if (videoRef.current && item.category === 'video') {
                        if (isHovered) {
                            videoRef.current.currentTime = 0;
                            videoRef.current.play().catch(err => {
                                console.log("Error al reproducir preview:", err);
                            });
                        } else {
                            videoRef.current.pause();
                        }
                    }
                }, [isHovered, item.category]);

                return (
                    <div
                        className={`overflow-hidden flex-shrink-0 bg-white rounded-xl shadow-md transition-all duration-300 cursor-pointer hover:shadow-lg ${item.category === 'video' || item.category === 'ebook' ? 'w-[200px] sm:w-[240px]' : 'w-[280px] sm:w-80'}`}
                        onClick={() => openPostModal(item)}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                    >
                        {/* Card Header */}
                        <div className="px-3 pt-2 pb-2 sm:px-4 sm:pt-3">
                            <div className="flex justify-between items-center text-xs text-gray-500">
                                <span>{date}</span>
                                <span>{time}</span>
                            </div>
                        </div>

                        {/* Thumbnail/Video Preview — contenido encajado sin recortar */}
                        <div className="flex overflow-hidden relative justify-center items-center w-full bg-gray-200 aspect-[9/16]">
                            {item.category === 'video' && fullVideoUrl && isHovered ? (
                                <video
                                    ref={videoRef}
                                    src={fullVideoUrl}
                                    className="object-contain max-w-full max-h-full w-full h-full"
                                    muted
                                    loop
                                    playsInline
                                    preload="metadata"
                                    onError={(e) => {
                                        // Si el video falla, mostrar imagen
                                        e.target.style.display = 'none';
                                        const img = e.target.nextElementSibling;
                                        if (img) img.style.display = 'block';
                                    }}
                                />
                            ) : null}
                            <img
                                src={thumbnail}
                                alt={title}
                                className={`object-contain max-w-full max-h-full w-full h-full ${item.category === 'video' && fullVideoUrl && isHovered ? 'hidden' : ''}`}
                                onError={(e) => {
                                    e.target.src = "/img/default.png";
                                }}
                            />
                            <div className={`flex absolute inset-0 justify-center items-center transition-all ${item.category === 'video' && isHovered ? 'bg-black/10' : 'bg-black/30'} hover:bg-black/20`}>
                                {item.category === 'video' ? (
                                    <div className={`flex justify-center items-center w-12 h-12 rounded-full sm:w-16 sm:h-16 bg-white/90 transition-opacity ${isHovered ? 'opacity-0' : 'opacity-100'}`}>
                                        <IoPlay className="ml-1 text-xl sm:text-2xl text-[#4DABA4]" />
                                    </div>
                                ) : null}
                            </div>
                        </div>

                        {/* Card Content */}
                        <div className="p-3 sm:p-4">
                            <h3 className="mb-2 text-base font-semibold text-gray-800 sm:text-lg line-clamp-2">
                                {title}
                            </h3>
                            
                            {item.teacherName && (
                                <p className="mb-1 text-xs text-gray-600 sm:text-sm">
                                    {item.teacherName}
                                </p>
                            )}
                            
                            {item.teacherRole && (
                                <p className="mb-2 text-xs text-gray-500">
                                    {item.teacherRole}
                                </p>
                            )}

                            <p className="mb-3 text-xs text-gray-600 sm:mb-4 sm:text-sm line-clamp-2">
                                {description || 'Sin descripción disponible'}
                            </p>

                            {/* Engagement Metrics */}
                            <div className="flex flex-wrap gap-2 justify-between items-center">
                                <div className="flex flex-wrap gap-2 items-center text-xs text-gray-600 sm:gap-4 sm:text-sm">
                                    <span>{item.views ?? 0} Vistas</span>
                                    <button
                                        onClick={(e) => handleToggleLike(item, e)}
                                        className={`flex items-center space-x-1 transition-colors ${
                                            getLikeFlag(item) ? 'text-[#4DABA4]' : 'text-gray-600 hover:text-[#4DABA4]'
                                        }`}
                                    >
                                        <IoThumbsUp className={getLikeFlag(item) ? 'text-[#4DABA4]' : 'text-gray-400'} />
                                        <span>{item.likes ?? 0}</span>
                                    </button>
                                </div>
                                                        <button
                                                            onClick={(e) => handleToggleCollection(item, e)}
                                                            className={`p-2 transition-colors ${getCollectionFlag(item) ? 'text-[#4DABA4]' : 'text-gray-400 hover:text-[#4DABA4]'}`}
                                                        >
                                    <SaveIcon saved={getCollectionFlag(item)} className="text-lg sm:text-xl" />
                                </button>
                            </div>
                        </div>
                    </div>
                );
            };

            return (
                item.category === 'video' ? (
                    <VideoPreviewCard 
                        key={item.id || index}
                        item={item}
                        thumbnail={thumbnail}
                        title={title}
                        description={description}
                        date={date}
                        time={time}
                    />
                ) : (
                    <div
                        key={item.id || index}
                        className={`overflow-hidden flex-shrink-0 bg-white rounded-xl shadow-md transition-all duration-300 cursor-pointer hover:shadow-lg ${item.category === 'video' || item.category === 'ebook' ? 'w-[200px] sm:w-[240px]' : 'w-[280px] sm:w-80'}`}
                        onClick={() => openPostModal(item)}
                    >
                        {/* Card Header */}
                        <div className="px-3 pt-2 pb-2 sm:px-4 sm:pt-3">
                            <div className="flex justify-between items-center text-xs text-gray-500">
                                <span>{date}</span>
                                <span>{time}</span>
                            </div>
                        </div>

                        {/* Thumbnail — imagen completa visible */}
                        <div className="flex relative justify-center items-center w-full bg-gray-200 aspect-[9/16]">
                            <img
                                src={thumbnail}
                                alt={title}
                                className="object-contain max-w-full max-h-full w-full h-full"
                                onError={(e) => {
                                    e.target.src = "/img/default.png";
                                }}
                            />
                            <div className="flex absolute inset-0 justify-center items-center transition-all bg-black/30 hover:bg-black/20">
                                {item.category === 'ebook' ? (
                                    <div className="flex justify-center items-center w-12 h-12 rounded-full sm:w-16 sm:h-16 bg-white/90">
                                        <IoBook className="text-xl sm:text-2xl text-[#4DABA4]" />
                                    </div>
                                ) : item.category === 'documento' ? (
                                    <div className="flex justify-center items-center w-12 h-12 rounded-full sm:w-16 sm:h-16 bg-white/90">
                                        <IoDocumentText className="text-xl sm:text-2xl text-[#4DABA4]" />
                                    </div>
                                ) : (
                                    <div className="flex justify-center items-center w-12 h-12 rounded-full sm:w-16 sm:h-16 bg-white/90">
                                        <IoNewspaper className="text-xl sm:text-2xl text-[#4DABA4]" />
                                    </div>
                                )}
                            </div>
                        </div>

                    {/* Card Content */}
                    <div className="p-3 sm:p-4">
                        <h3 className="mb-2 text-base font-semibold text-gray-800 sm:text-lg line-clamp-2">
                            {title}
                        </h3>
                        
                        {item.teacherName && (
                            <p className="mb-1 text-xs text-gray-600 sm:text-sm">
                                {item.teacherName}
                            </p>
                        )}
                        
                        {item.teacherRole && (
                            <p className="mb-2 text-xs text-gray-500">
                                {item.teacherRole}
                            </p>
                        )}

                        <p className="mb-3 text-xs text-gray-600 sm:mb-4 sm:text-sm line-clamp-2">
                            {description || 'Sin descripción disponible'}
                        </p>

                        {/* Engagement Metrics */}
                        <div className="flex flex-wrap gap-2 justify-between items-center">
                            <div className="flex flex-wrap gap-2 items-center text-xs text-gray-600 sm:gap-4 sm:text-sm">
                                <span>{item.views ?? 0} Vistas</span>
                                <button
                                    onClick={(e) => handleToggleLike(item, e)}
                                    className={`flex items-center space-x-1 transition-colors ${
                                        getLikeFlag(item) ? 'text-[#4DABA4]' : 'text-gray-600 hover:text-[#4DABA4]'
                                    }`}
                                >
                                    <IoThumbsUp className={getLikeFlag(item) ? 'text-[#4DABA4]' : 'text-gray-400'} />
                                    <span>{item.likes ?? 0}</span>
                                </button>
                            </div>
                            <button
                                onClick={(e) => handleToggleCollection(item, e)}
                                className={`p-2 transition-colors ${getCollectionFlag(item) ? 'text-[#4DABA4]' : 'text-gray-400 hover:text-[#4DABA4]'}`}
                            >
                                <SaveIcon saved={getCollectionFlag(item)} className="text-lg sm:text-xl" />
                            </button>
                        </div>
                    </div>
                    </div>
                )
            );
        };

        const isVideoOrEbook = category === 'video' || category === 'ebook';
        
        return (
            <div className={`mb-6 sm:mb-8 ${isVideoOrEbook ? 'p-4 bg-gray-100 rounded-lg sm:p-6' : ''}`}>
                <div className={`flex flex-col gap-2 mb-3 sm:flex-row sm:justify-between sm:items-center sm:mb-4 ${isVideoOrEbook ? 'px-0' : ''}`}>
                    <div className="flex flex-wrap gap-2 items-center sm:gap-3">
                        {isVideoOrEbook ? (
                            <h2 className="text-lg font-bold text-gray-800 sm:text-xl">{categoryName}</h2>
                        ) : (
                            <>
                                <div className={`inline-flex items-center px-2 py-1 rounded-lg sm:px-3 ${categoryColor}`}>
                                    <span className="text-base sm:text-xl">{categoryIcon}</span>
                                    <h2 className="ml-1 text-lg font-bold sm:ml-2 sm:text-xl">{categoryName}</h2>
                                </div>
                                <span className="text-xs text-gray-500 sm:text-sm">({items.length})</span>
                            </>
                        )}
                    </div>
                </div>

                {viewMode === "list" ? (
                    // Vista de lista - Layout especial para desktop
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 lg:gap-6">
                        {/* Video principal - Solo en desktop */}
                        {items.length > 0 && (
                            <div className="hidden lg:block lg:col-span-2">
                                {(() => {
                                    const mainItem = items[0];
                                    const mainThumbnail = mainItem.thumbnail || mainItem.image || "/img/default.png";
                                    const mainTitle = stripHtml(mainItem.title || 'Sin título');
                                    const mainDescription = stripHtml(mainItem.description || '');
                                    const mainDate = formatDate(mainItem.createdAt || mainItem.createdDate);
                                    const mainTime = formatTime(mainItem.createdAt || mainItem.createdDate);
                                    
                                    return (
                                        <div
                                            className="overflow-hidden bg-white rounded-xl shadow-md transition-all duration-300 cursor-pointer hover:shadow-lg"
                                            onClick={() => openPostModal(mainItem)}
                                        >
                                            {/* Header con fecha */}
                                            <div className="px-4 pt-4 pb-2">
                                                <div className="flex justify-between items-center text-xs text-gray-500">
                                                    <span>{mainDate}</span>
                                                    <span>{mainTime}</span>
                                                </div>
                                            </div>

                                            {/* Thumbnail principal — imagen completa visible */}
                                            <div className="flex relative justify-center items-center w-full bg-gray-200 aspect-[9/16]">
                                                <img
                                                    src={mainThumbnail}
                                                    alt={mainTitle}
                                                    className="object-contain max-w-full max-h-full w-full h-full"
                                                    onError={(e) => {
                                                        e.target.src = "/img/default.png";
                                                    }}
                                                />
                                                <div className="flex absolute inset-0 justify-center items-center transition-all bg-black/30 hover:bg-black/20">
                                                    {mainItem.category === 'video' ? (
                                                        <div className="flex justify-center items-center w-20 h-20 rounded-full bg-white/90">
                                                            <IoPlay className="ml-1 text-3xl text-[#4DABA4]" />
                                                        </div>
                                                    ) : mainItem.category === 'ebook' ? (
                                                        <div className="flex justify-center items-center w-20 h-20 rounded-full bg-white/90">
                                                            <IoBook className="text-3xl text-[#4DABA4]" />
                                                        </div>
                                                    ) : mainItem.category === 'documento' ? (
                                                        <div className="flex justify-center items-center w-20 h-20 rounded-full bg-white/90">
                                                            <IoDocumentText className="text-3xl text-[#4DABA4]" />
                                                        </div>
                                                    ) : (
                                                        <div className="flex justify-center items-center w-20 h-20 rounded-full bg-white/90">
                                                            <IoNewspaper className="text-3xl text-[#4DABA4]" />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Contenido principal */}
                                            <div className="p-4">
                                                <h3 className="mb-2 text-xl font-bold text-gray-800 line-clamp-2">
                                                    {mainTitle}
                                                </h3>
                                                
                                                {mainItem.teacherName && (
                                                    <div className="mb-2">
                                                        <p className="text-sm font-semibold text-[#4DABA4]">
                                                            {mainItem.teacherName}
                                                        </p>
                                                        {mainItem.teacherRole && (
                                                            <p className="text-xs text-gray-500">
                                                                {mainItem.teacherRole}
                                                            </p>
                                                        )}
                                                    </div>
                                                )}

                                                <p className="mb-4 text-sm text-gray-600 line-clamp-3">
                                                    {mainDescription || 'Sin descripción disponible'}
                                                </p>

                                                {/* Engagement Metrics */}
                                                <div className="flex justify-between items-center">
                                                    <div className="flex gap-4 items-center text-sm text-gray-600">
                                                        <span>{mainItem.views ?? 0} Vistas</span>
                                                        <button
                                                            onClick={(e) => handleToggleLike(mainItem, e)}
                                                            className={`flex items-center space-x-1 transition-colors ${
                                                                getLikeFlag(mainItem) ? 'text-[#4DABA4]' : 'text-gray-600 hover:text-[#4DABA4]'
                                                            }`}
                                                        >
                                                            <IoThumbsUp className={getLikeFlag(mainItem) ? 'text-[#4DABA4]' : 'text-gray-400'} />
                                                            <span>{mainItem.likes ?? 0}</span>
                                                        </button>
                                                    </div>
                                        <button
                                            onClick={(e) => handleToggleCollection(mainItem, e)}
                                            className={`p-2 transition-colors ${getCollectionFlag(mainItem) ? 'text-[#4DABA4]' : 'text-gray-400 hover:text-[#4DABA4]'}`}
                                        >
                                                        <SaveIcon saved={getCollectionFlag(mainItem)} className="text-xl" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                        )}

                        {/* Lista de videos - Sidebar en desktop, lista completa en móvil */}
                        <div className="lg:col-span-1">
                            <div className="space-y-0 max-h-[800px] overflow-y-auto lg:max-h-none pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                                {items.slice(isDesktop ? 1 : 0).map((item, index) => renderCard(item, index))}
                            </div>
                        </div>
                    </div>
                ) : (
                    // Vista de grid (carrusel)
                    <div className={`relative ${isVideoOrEbook ? '':''}`}>
                        {/* Scroll Container */}
                        <div 
                            id={`scroll-${category}`}
                            className="flex overflow-x-auto pb-4 space-x-3 sm:space-x-4 scrollbar-hide scroll-smooth"
                            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                        >
                            {items.map((item, index) => renderCard(item, index))}
                        </div>

                        {/* Scroll Buttons - Solo en vista grid y desktop */}
                        {items.length > 0 && viewMode === "grid" && (
                            <>
                                <button
                                    onClick={() => scrollContainer('left', category)}
                                    className="hidden absolute left-0 top-1/2 z-10 p-2 bg-white rounded-full shadow-lg transition-all -translate-x-2 -translate-y-1/2 md:flex md:-translate-x-4 hover:shadow-xl"
                                    aria-label="Scroll izquierda"
                                >
                                    <IoChevronForward className="text-xl md:text-2xl text-[#4DABA4] rotate-180" />
                                </button>
                                <button
                                    onClick={() => scrollContainer('right', category)}
                                    className="hidden absolute right-0 top-1/2 z-10 p-2 bg-white rounded-full shadow-lg transition-all translate-x-2 -translate-y-1/2 md:flex md:translate-x-4 hover:shadow-xl"
                                    aria-label="Scroll derecha"
                                >
                                    <IoChevronForward className="text-xl md:text-2xl text-[#4DABA4]" />
                                </button>
                            </>
                        )}
                    </div>
                )}
        </div>
    );
    };

     if (showSplash && isFirstVisit) {
        return <SplashScreen onComplete={handleSplashComplete} />;
    }
    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header condicional: Navbar si está logueado, header simple si no */}
            {user ? (
                <Navbar withoutOutlet={true} />
            ) : (
                <header className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
                    <div className="flex justify-between items-center max-w-7xl mx-auto w-full px-4 py-3.5 md:px-6">
                        <div className="flex min-w-0 items-center">
                            <img 
                                src="/icons/Logo completo.png" 
                                alt="Docentia" 
                                className="h-10 object-contain flex-shrink-0 md:h-12 cursor-pointer transition-opacity hover:opacity-80"
                                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                            />
                        </div>
                        <button 
                            onClick={openModal} 
                            className="flex-shrink-0 px-5 py-2.5 h-10 background-green-aqua text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 text-sm md:text-base flex items-center justify-center"
                        >
                            Ingresar
                        </button>
                    </div>
                </header>
            )}
            
            {/* Modal de login - renderizado fuera del header para que funcione en ambos casos */}
            {isModalOpen && !user && <Modal closeModal={closeModal} />}

            {/* Hero Section */}
            <section className="py-6 w-full sm:py-8">
                <SwiperCarousel items={carouselItems} onLoginRequired={openModal} />
            </section>

            {/* Navigation and Filters */}
            <section className="relative z-30 bg-white border-b border-gray-200">
                <div className="container px-4 py-4 mx-auto">
                    {welcomeBlocked && activeTab === "comunidad" && (
                        <WelcomeCourseBanner
                            courseId={welcomeCourseId}
                            courseTitle={welcomeCourseTitle}
                        />
                    )}

                    {/* Main Tabs */}
                    <div className="flex flex-nowrap items-center gap-2 mb-4 sm:gap-4">
                        <div className="flex flex-nowrap items-center gap-2 overflow-x-auto sm:gap-4 min-w-0">
                        <button
                            onClick={() => setActiveTab("comunidad")}
                            className={`inline-flex flex-shrink-0 items-center justify-center px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base font-semibold rounded-lg transition-all whitespace-nowrap border ${
                                activeTab === "comunidad"
                                    ? 'background-green-aqua text-white shadow-md border-transparent'
                                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                            }`}
                        >
                            Comunidad
                        </button>
                        <button 
                            onClick={() => setActiveTab("cursos")}
                            className={`inline-flex flex-shrink-0 items-center justify-center px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base font-semibold rounded-lg transition-all whitespace-nowrap border ${
                                activeTab === "cursos"
                                    ? 'background-green-aqua text-white shadow-md border-transparent'
                                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                            }`}
                        >
                            Cursos
                        </button>
                        </div>
                        {user && (
                            <div className="relative z-[100] flex-shrink-0">
                                <button 
                                    onClick={() => setActiveTab("mis-cursos")}
                                    className={`inline-flex items-center justify-center px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base font-semibold rounded-lg transition-all whitespace-nowrap border ${
                                        activeTab === "mis-cursos"
                                            ? 'background-green-aqua text-white shadow-md border-transparent'
                                            : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                                    }`}
                                >
                                    Mis cursos
                                </button>
                                {recentCoursesCount > 0 && (
                                    <span className="absolute -top-2 -right-2 z-[100] flex justify-center items-center min-w-[20px] h-5 px-1.5 text-xs font-bold text-white bg-red-500 rounded-full border-2 border-white pointer-events-none">
                                        {recentCoursesCount}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Filters and Search - Solo para tab Comunidad */}
                    {activeTab === "comunidad" && (
                    <div className="flex flex-wrap gap-2 items-center sm:gap-4">
                        <div className="relative z-50 w-full sm:w-auto">
                            <button 
                                data-category-button
                                onClick={() => {
                                    setIsCategoryMenuOpen(!isCategoryMenuOpen);
                                    setIsSortMenuOpen(false);
                                    setIsCollectionMenuOpen(false);
                                }}
                                className="flex justify-between items-center px-3 py-2 space-x-2 w-full text-sm bg-white rounded-lg border border-[#4DABA4] sm:justify-start sm:w-auto sm:px-4 sm:text-base hover:bg-gray-50"
                            >
                                <span className="font-medium text-gray-700 truncate">
                                    {selectedCategory === "todas" 
                                        ? "Explorar por Categorias" 
                                        : selectedSubCategory 
                                            ? `${getCategoryName(selectedCategory)} - ${selectedSubCategory}`
                                            : getCategoryName(selectedCategory)}
                                </span>
                                <HiOutlineChevronDown className={`text-base sm:text-lg flex-shrink-0 transition-transform text-[#4DABA4] ${isCategoryMenuOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {isCategoryMenuOpen && (
                                <div 
                                    data-category-menu 
                                    className="absolute z-[100] mt-2 w-full sm:w-64 bg-white rounded-lg border border-gray-200 shadow-xl overflow-hidden"
                                    style={{ position: 'absolute', top: '100%', left: 0 }}
                                >
                                    {/* Novedades - Opción simple */}
                                    <button
                                        onClick={() => {
                                            setSelectedCategory("todas");
                                            setSelectedSubCategory(null);
                                            setIsCategoryMenuOpen(false);
                                        }}
                                        className={`block w-full text-left px-4 py-3 text-sm border-l-4 ${
                                            selectedCategory === "todas" && !selectedSubCategory
                                                ? 'bg-[#4DABA4] text-white border-l-[#4DABA4] font-semibold' 
                                                : 'text-gray-500 hover:bg-gray-50 border-l-transparent'
                                        }`}
                                    >
                                        Novedades
                                    </button>
                                    
                                    {/* Separador */}
                                    <div className="border-t border-gray-200"></div>
                                    
                                    {/* Línea Terapéutica - Categoría expandible */}
                                    <div>
                                        <button
                                            onClick={() => {
                                                setExpandedCategory(expandedCategory === "terapeutica" ? null : "terapeutica");
                                            }}
                                            className={`flex justify-between items-center w-full text-left px-4 py-3 text-sm border-l-4 ${
                                                selectedCategory === "terapeutica"
                                                    ? 'bg-[#4DABA4] text-white border-l-[#4DABA4] font-semibold' 
                                                    : 'text-[#4DABA4] hover:bg-gray-50 border-l-transparent'
                                            }`}
                                        >
                                            <span className="font-medium">Línea Terapéutica</span>
                                            <HiOutlineChevronDown className={`text-base flex-shrink-0 transition-transform ${expandedCategory === "terapeutica" ? 'rotate-180' : ''} ${selectedCategory === "terapeutica" ? 'text-white' : 'text-[#4DABA4]'}`} />
                                        </button>
                                        {expandedCategory === "terapeutica" && (
                                            <div className="bg-gray-50 border-l-4 border-l-[#4DABA4]">
                                                {[
                                                    "Gastroenterología",
                                                    "Dermatología",
                                                    "Neurociencias",
                                                    "Alivio del dolor",
                                                    "Oftalmología - Poen",
                                                    "Cardiología",
                                                    "Respiratoria",
                                                    "Estética - Croma",
                                                    "Alto costo - Iclos",
                                                    "Alimentación Especializada – Victus",
                                                    "OTC"
                                                ].map((subCat) => (
                                                    <button
                                                        key={subCat}
                                                        onClick={() => {
                                                            setSelectedCategory("terapeutica");
                                                            setSelectedSubCategory(subCat);
                                                            setIsCategoryMenuOpen(false);
                                                            setExpandedCategory(null);
                                                        }}
                                                        className={`block w-full text-left px-8 py-2 text-sm text-gray-600 hover:bg-gray-100 ${
                                                            selectedCategory === "terapeutica" && selectedSubCategory === subCat
                                                                ? 'bg-[#4DABA4]/20 text-[#4DABA4] font-medium'
                                                                : ''
                                                        }`}
                                                    >
                                                        {subCat}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Separador */}
                                    <div className="border-t border-gray-200"></div>
                                    
                                    {/* Productos - Categoría expandible */}
                                    <div>
                                        <button
                                            onClick={() => {
                                                setExpandedCategory(expandedCategory === "productos" ? null : "productos");
                                            }}
                                            className={`flex justify-between items-center w-full text-left px-4 py-3 text-sm border-l-4 ${
                                                selectedCategory === "productos"
                                                    ? 'bg-[#4DABA4] text-white border-l-[#4DABA4] font-semibold' 
                                                    : 'text-[#4DABA4] hover:bg-gray-50 border-l-transparent'
                                            }`}
                                        >
                                            <span className="font-medium">Productos</span>
                                            <HiOutlineChevronDown className={`text-base flex-shrink-0 transition-transform ${expandedCategory === "productos" ? 'rotate-180' : ''} ${selectedCategory === "productos" ? 'text-white' : 'text-[#4DABA4]'}`} />
                                        </button>
                                        {expandedCategory === "productos" && (
                                            <div className="bg-gray-50 border-l-4 border-l-[#4DABA4]">
                                                {[
                                                    "Umbrella",
                                                    "Edapil",
                                                    "Ciruelax",
                                                    "Umquan",
                                                    "Abrilar"
                                                ].map((subCat) => (
                                                    <button
                                                        key={subCat}
                                                        onClick={() => {
                                                            setSelectedCategory("productos");
                                                            setSelectedSubCategory(subCat);
                                                            setIsCategoryMenuOpen(false);
                                                            setExpandedCategory(null);
                                                        }}
                                                        className={`block w-full text-left px-8 py-2 text-sm text-gray-600 hover:bg-gray-100 border-t border-gray-200 first:border-t-0 ${
                                                            selectedCategory === "productos" && selectedSubCategory === subCat
                                                                ? 'bg-[#4DABA4]/20 text-[#4DABA4] font-medium'
                                                                : ''
                                                        }`}
                                                    >
                                                        {subCat}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Separador */}
                                    <div className="border-t border-gray-200"></div>
                                                                        
                                    {/* Quitar filtro */}
                                    <button
                                        onClick={() => {
                                            setSelectedCategory("todas");
                                            setSelectedSubCategory(null);
                                            setIsCategoryMenuOpen(false);
                                        }}
                                        className="block px-4 py-3 w-full text-sm text-left text-gray-400 rounded-b-lg hover:text-gray-600 hover:bg-gray-50"
                                    >
                                        Quitar filtro
                                    </button>
                                </div>
                            )}
                        </div>
                        {/* Dropdown Mi Colección - Solo visible si el usuario está logueado */}
                        {user && (
                            <div className="relative z-50 w-full sm:w-auto">
                                <button 
                                    data-collection-button
                                    onClick={() => {
                                        setIsCollectionMenuOpen(!isCollectionMenuOpen);
                                        setIsCategoryMenuOpen(false);
                                        setIsSortMenuOpen(false);
                                    }}
                                    className="flex justify-between items-center px-3 py-2 space-x-2 w-full text-sm bg-white rounded-lg border border-[#4DABA4] sm:justify-start sm:w-auto sm:px-4 sm:text-base hover:bg-gray-50"
                                >
                                    <span className="font-medium text-gray-700 truncate">
                                        {selectedCollection === "guardados" && "Guardados"}
                                        {selectedCollection === "me-gusta" && "Me gusta"}
                                        {selectedCollection === "comentarios" && "Comentarios"}
                                        {!selectedCollection && "Mi Colección"}
                                    </span>
                                    <HiOutlineChevronDown className={`text-base sm:text-lg flex-shrink-0 transition-transform text-[#4DABA4] ${isCollectionMenuOpen ? 'rotate-180' : ''}`} />
                                </button>
                                {isCollectionMenuOpen && (
                                    <div 
                                        data-collection-menu 
                                        className="absolute z-[100] mt-2 w-full sm:w-48 bg-white rounded-lg border border-gray-200 shadow-xl overflow-hidden"
                                        style={{ position: 'absolute', top: '100%', left: 0 }}
                                    >
                                        <button
                                            onClick={() => {
                                                setSelectedCollection(selectedCollection === "guardados" ? null : "guardados");
                                                setIsCollectionMenuOpen(false);
                                            }}
                                            className={`flex items-center justify-between w-full text-left px-4 py-3 text-sm border-l-4 ${
                                                selectedCollection === "guardados"
                                                    ? 'bg-[#4DABA4]/10 text-[#4DABA4] border-l-[#4DABA4] font-semibold' 
                                                    : 'text-gray-700 hover:bg-gray-50 border-l-transparent'
                                            }`}
                                        >
                                            <div className="flex items-center space-x-2">
                                                <IoBookmarkOutline className="text-lg" />
                                                <span>Guardados</span>
                                            </div>
                                            {selectedCollection === "guardados" && (
                                                <div className="w-2 h-2 bg-yellow-400 rounded-sm"></div>
                                            )}
                                        </button>
                                        <button
                                            onClick={() => {
                                                setSelectedCollection(selectedCollection === "me-gusta" ? null : "me-gusta");
                                                setIsCollectionMenuOpen(false);
                                            }}
                                            className={`flex items-center w-full text-left px-4 py-3 text-sm border-l-4 ${
                                                selectedCollection === "me-gusta"
                                                    ? 'bg-[#4DABA4]/10 text-[#4DABA4] border-l-[#4DABA4] font-semibold' 
                                                    : 'text-gray-700 hover:bg-gray-50 border-l-transparent'
                                            }`}
                                        >
                                            <div className="flex items-center space-x-2">
                                                <IoHeart className="text-lg" />
                                                <span>Me gusta</span>
                                            </div>
                                        </button>
                                        <button
                                            onClick={() => {
                                                setSelectedCollection(selectedCollection === "comentarios" ? null : "comentarios");
                                                setIsCollectionMenuOpen(false);
                                            }}
                                            className={`flex items-center w-full text-left px-4 py-3 text-sm border-l-4 ${
                                                selectedCollection === "comentarios"
                                                    ? 'bg-[#4DABA4]/10 text-[#4DABA4] border-l-[#4DABA4] font-semibold' 
                                                    : 'text-gray-700 hover:bg-gray-50 border-l-transparent'
                                            }`}
                                        >
                                            <div className="flex items-center space-x-2">
                                                <IoChatbubbleOutline className="text-lg" />
                                                <span>Comentarios</span>
                                            </div>
                                        </button>
                                        {selectedCollection && (
                                            <>
                                                <div className="border-t border-gray-200"></div>
                                                <button
                                                    onClick={() => {
                                                        setSelectedCollection(null);
                                                        setIsCollectionMenuOpen(false);
                                                    }}
                                                    className="block px-4 py-3 w-full text-sm text-left text-gray-400 rounded-b-lg hover:text-gray-600 hover:bg-gray-50"
                                                >
                                                    Quitar Filtro
                                                </button>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="relative z-50 w-full sm:w-auto">
                            <button 
                                data-sort-button
                                onClick={() => {
                                    setIsSortMenuOpen(!isSortMenuOpen);
                                    setIsCategoryMenuOpen(false);
                                    setIsCollectionMenuOpen(false);
                                }}
                                className="flex justify-between items-center px-3 py-2 space-x-2 w-full text-sm bg-white rounded-lg border border-[#4DABA4] sm:justify-start sm:w-auto sm:px-4 sm:text-base hover:bg-gray-50"
                            >
                                <span className="font-medium text-gray-700 truncate">
                                    {sortBy === "recientes" && "Ordenar por"}
                                    {sortBy === "antiguos" && "Más antiguo en la parte superior"}
                                    {sortBy === "mas-vistos" && "Más vistos"}
                                    {sortBy === "menos-vistos" && "Menos vistos"}
                                    {sortBy === "mas-likes" && "Mejor Calificados"}
                                    {sortBy === "a-z" && "A-Z"}
                                    {sortBy === "z-a" && "Z-A"}
                                </span>
                                <HiOutlineChevronDown className={`text-base sm:text-lg flex-shrink-0 transition-transform text-[#4DABA4] ${isSortMenuOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {isSortMenuOpen && (
                                <div 
                                    data-sort-menu 
                                    className="absolute z-[100] mt-2 w-full sm:w-48 bg-white rounded-lg border-l-4 border-l-[#4DABA4] border border-gray-200 shadow-xl"
                                    style={{ position: 'absolute', top: '100%', left: 0 }}
                                >
                                    <button
                                        onClick={() => {
                                            setSortBy("recientes");
                                            setIsSortMenuOpen(false);
                                        }}
                                        className={`block w-full text-left px-4 py-2 text-sm rounded-t-lg border-l-4 ${
                                            sortBy === "recientes" 
                                                ? 'bg-[#4DABA4] text-white border-l-[#4DABA4] font-semibold' 
                                                : 'text-gray-700 hover:bg-gray-50 border-l-transparent'
                                        }`}
                                    >
                                        Más reciente en la parte superior
                                    </button>
                                    <button
                                        onClick={() => {
                                            setSortBy("antiguos");
                                            setIsSortMenuOpen(false);
                                        }}
                                        className={`block w-full text-left px-4 py-2 text-sm border-l-4 ${
                                            sortBy === "antiguos" 
                                                ? 'bg-[#4DABA4] text-white border-l-[#4DABA4] font-semibold' 
                                                : 'text-gray-700 hover:bg-gray-50 border-l-transparent'
                                        }`}
                                    >
                                        Más antiguo en la parte superior
                                    </button>
                                    <button
                                        onClick={() => {
                                            setSortBy("mas-vistos");
                                            setIsSortMenuOpen(false);
                                        }}
                                        className={`block w-full text-left px-4 py-2 text-sm border-l-4 ${
                                            sortBy === "mas-vistos" 
                                                ? 'bg-[#4DABA4] text-white border-l-[#4DABA4] font-semibold' 
                                                : 'text-gray-700 hover:bg-gray-50 border-l-transparent'
                                        }`}
                                    >
                                        Más vistos
                                    </button>
                                    <button
                                        onClick={() => {
                                            setSortBy("mas-likes");
                                            setIsSortMenuOpen(false);
                                        }}
                                        className={`block w-full text-left px-4 py-2 text-sm border-l-4 ${
                                            sortBy === "mas-likes" 
                                                ? 'bg-[#4DABA4] text-white border-l-[#4DABA4] font-semibold' 
                                                : 'text-gray-700 hover:bg-gray-50 border-l-transparent'
                                        }`}
                                    >
                                        Mejor Calificados
                                    </button>
                                    <button
                                        onClick={() => {
                                            setSortBy("recientes");
                                            setIsSortMenuOpen(false);
                                        }}
                                        className="block px-4 py-2 w-full text-sm text-left text-gray-700 rounded-b-lg border-l-4 hover:bg-gray-50 border-l-transparent"
                                    >
                                        Quitar Filtro
                                    </button>
                                            </div>
                            )}
                                        </div>
                        <div className="flex flex-1 items-center px-3 sm:px-4 py-2 space-x-2 bg-white border border-gray-300 rounded-lg min-w-0 w-full sm:min-w-[200px] sm:w-auto">
                            <IoSearch className="flex-shrink-0 text-lg text-gray-400 sm:text-xl" />
                            <input
                                type="text"
                                placeholder="Buscar"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="flex-1 min-w-0 text-sm outline-none sm:text-base"
                            />
                        </div>
                        <div className="flex items-center space-x-1 sm:space-x-2">
                            <button
                                onClick={() => setViewMode("grid")}
                                className={`p-2 rounded-lg transition-all ${
                                    viewMode === "grid"
                                        ? 'background-green-aqua text-white'
                                        : 'text-gray-600 hover:bg-gray-100'
                                }`}
                                aria-label="Vista de cuadrícula"
                            >
                                <IoGrid className="text-lg sm:text-xl" />
                            </button>
                            <button
                                onClick={() => setViewMode("list")}
                                className={`p-2 rounded-lg transition-all ${
                                    viewMode === "list"
                                        ? 'background-green-aqua text-white'
                                        : 'text-gray-600 hover:bg-gray-100'
                                }`}
                                aria-label="Vista de lista"
                            >
                                <IoList className="text-lg sm:text-xl" />
                            </button>
                        </div>
                    </div>
                    )}
                </div>
            </section>

            {/* Content Section - Multiple Carousels by Category or Courses */}
            <section className="container relative px-4 py-6 mx-auto">
                {activeTab === "mis-cursos" && user ? (
                    // Vista de Mis Cursos
                    <MyCourses key={myCoursesRefreshKey} />
                ) : activeTab === "cursos" ? (
                    // Vista de Cursos Disponibles - Estilo simplificado
                    <div>
                        {/* Buscador discreto en la esquina superior derecha */}
                        <div className="flex justify-end mb-6">
                            <div className="flex items-center px-3 py-2 bg-white rounded-lg border border-gray-200 shadow-sm transition-shadow hover:shadow-md">
                                <IoSearch className="mr-2 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Buscar"
                                    value={courseSearchTerm}
                                    onChange={(e) => setCourseSearchTerm(e.target.value)}
                                    className="w-40 text-sm outline-none"
                                />
                            </div>
                        </div>
                        
                        {/* Renderizar Cview con estilo compacto */}
                        <div>
                            <Cview 
                                compact={true} 
                                externalSearchTerm={courseSearchTerm} 
                                onSearchChange={setCourseSearchTerm}
                                onLoginRequired={openModal}
                                onEnrollSuccess={handleCourseEnrollSuccess}
                            />
                        </div>
                    </div>
                ) : (
                    // Vista de Comunidad
                    loading ? (
                        <div className="flex justify-center items-center py-12">
                            <div className="w-12 h-12 border-4 border-[#4DABA4] border-t-transparent rounded-full animate-spin"></div>
                        </div>
                    ) : Object.keys(postsByCategory).length === 0 ? (
                        <div className="flex justify-center items-center py-12">
                            <p className="text-gray-500">No hay contenido disponible</p>
                        </div>
                    ) : (
                        <div>
                            {/* Carrusel de Videos */}
                            {postsByCategory.video && (
                                <CategoryCarousel
                                    category="video"
                                    items={postsByCategory.video}
                                    categoryName="Videos"
                                    categoryColor="bg-red-100 text-red-700"
                                    categoryIcon={<IoVideocam className="text-xl" />}
                                    viewMode={viewMode}
                                />
                            )}

                            {/* Carrusel de Ebooks */}
                            {postsByCategory.ebook && (
                                <CategoryCarousel
                                    category="ebook"
                                    items={postsByCategory.ebook}
                                    categoryName="Ebooks"
                                    categoryColor="bg-blue-100 text-blue-700"
                                    categoryIcon={<IoBook className="text-xl" />}
                                    viewMode={viewMode}
                                />
                            )}

                            {/* Carrusel de Documentos */}
                            {postsByCategory.documento && (
                                <CategoryCarousel
                                    category="documento"
                                    items={postsByCategory.documento}
                                    categoryName="Documentos"
                                    categoryColor="bg-yellow-100 text-yellow-700"
                                    categoryIcon={<IoDocumentText className="text-xl" />}
                                    viewMode={viewMode}
                                />
                            )}

                            {/* Carrusel de Noticias */}
                            {postsByCategory.noticia && (
                                <CategoryCarousel
                                    category="noticia"
                                    items={postsByCategory.noticia}
                                    categoryName="Noticias"
                                    categoryColor="bg-green-100 text-green-700"
                                    categoryIcon={<IoNewspaper className="text-xl" />}
                                    viewMode={viewMode}
                                />
                            )}
                        </div>
                    )
                )}
            </section>

            {/* Modal de Post Completo */}
            {isPostModalOpen && postContent && (
                <div 
                    className="fixed inset-0 z-[200] flex justify-center items-center p-4 bg-black bg-opacity-80 backdrop-blur-sm animate-fadeIn"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            closePostModal();
                        }
                    }}
                >
                    <div 
                        className="bg-white w-full max-w-7xl rounded-2xl shadow-2xl max-h-[95vh] overflow-hidden flex flex-col animate-slideUp"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header del Modal */}
                        <div className="flex justify-between items-center px-6 py-4 bg-gradient-to-r from-gray-50 to-white border-b-2 border-gray-100">
                            <div className="flex items-center space-x-3">
                                <div className="flex items-center space-x-2 px-3 py-1.5 bg-[#4DABA4] bg-opacity-10 rounded-lg">
                                    {postContent.type === 'video' && <IoVideocam className="text-lg text-[#4DABA4]" />}
                                    {postContent.type === 'pdf' && <IoDocumentText className="text-lg text-[#4DABA4]" />}
                                    {postContent.type === 'ebook' && <IoBook className="text-lg text-[#4DABA4]" />}
                                    {postContent.type === 'noticia' && <IoNewspaper className="text-lg text-[#4DABA4]" />}
                                    <span className="text-xs font-semibold text-[#4DABA4] uppercase tracking-wide">
                                        {postContent.type === 'video' && 'Video'}
                                        {postContent.type === 'pdf' && 'Documento PDF'}
                                        {postContent.type === 'ebook' && 'Ebook'}
                                        {postContent.type === 'noticia' && 'Noticia'}
                                    </span>
                                </div>
                                <div className="flex items-center space-x-2 text-xs text-gray-500">
                                    <span className="font-medium">{formatDate(postContent.createdAt || postContent.createdDate)}</span>
                                    <span className="text-gray-400">•</span>
                                    <span>{formatTime(postContent.createdAt || postContent.createdDate)}</span>
                                </div>
                            </div>
                            <button
                                onClick={closePostModal}
                                className="p-2.5 text-gray-500 rounded-xl transition-all duration-200 hover:text-gray-700 hover:bg-gray-100 hover:scale-110"
                                aria-label="Cerrar"
                            >
                                <IoClose className="text-2xl" />
                            </button>
                        </div>

                        {/* Contenido del Modal */}
                        <div className="overflow-y-auto flex-1 custom-scrollbar">
                            {loadingPost ? (
                                <div className="flex flex-col justify-center items-center py-20">
                                    <div className="w-16 h-16 border-4 border-[#4DABA4] border-t-transparent rounded-full animate-spin mb-4"></div>
                                    <p className="text-sm font-medium text-gray-500">Cargando contenido...</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 gap-0 lg:grid-cols-3">
                                    {/* Panel Principal - Video/PDF */}
                                    <div className="bg-gradient-to-br from-gray-50 via-white to-gray-50 lg:col-span-2">
                                        {postContent.type === 'video' ? (
                                            postContent.videoUrl ? (
                                                <div className="flex relative justify-center items-center w-full bg-black aspect-video">
                                                    <video
                                                        src={postContent.videoUrl}
                                                        controls
                                                        autoPlay
                                                        className="object-contain w-full h-full"
                                                        preload="auto"
                                                    >
                                                        Tu navegador no soporta el elemento de video.
                                                    </video>
                                                </div>
                                            ) : (
                                                <div className="flex flex-col justify-center items-center p-20 bg-gray-100">
                                                    <IoVideocam className="mb-4 text-6xl text-gray-400" />
                                                    <p className="font-medium text-gray-600">Video no disponible</p>
                                                    <p className="mt-2 text-sm text-gray-500">El video no se pudo cargar</p>
                                                </div>
                                            )
                                        ) : postContent.type === 'pdf' ? (
                                            postContent.fileUrl ? (
                                                <div className="flex relative flex-col w-full bg-gradient-to-br from-gray-50 to-gray-100">
                                                    {/* Header personalizado del visor PDF */}
                                                    <div className="flex flex-shrink-0 justify-between items-center px-4 py-3 shadow-md background-green-aqua">
                                                    <div className="flex items-center space-x-3">
                                                        <IoBook className="text-2xl text-white" />
                                                        <div>
                                                            <h3 className="text-sm font-semibold text-white">
                                                                {postContent.category === 'ebook' ? 'Ebook' : 'Documento'}
                                                            </h3>
                                                            <p className="text-xs text-white text-opacity-90">
                                                                {stripHtml(postContent.title || selectedPost?.title || 'Documento')}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    {totalPages > 1 && (
                                                        <div className="flex items-center space-x-2 px-3 py-1.5 bg-white bg-opacity-20 rounded-lg backdrop-blur-sm">
                                                            <span className="text-xs font-medium text-white">Página</span>
                                                            <span className="text-sm font-bold text-white">{currentPage}</span>
                                                            <span className="text-xs text-white text-opacity-80">de {totalPages}</span>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Contenedor del PDF con formato 9:16 */}
                                                <div className="relative w-full bg-white aspect-[9/16] flex-shrink-0">
                                                    <iframe
                                                        src={`${postContent.fileUrl}#page=${currentPage}&toolbar=0&navpanes=0&scrollbar=1`}
                                                        className="w-full h-full border-0"
                                                        title="PDF Viewer"
                                                    />
                                                </div>

                                                {/* Controles de navegación PDF personalizados */}
                                                {totalPages > 1 && (
                                                    <div className="flex justify-between items-center px-4 py-4 bg-white border-t-2 border-[#4DABA4] shadow-lg">
                                                        <button
                                                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                                            disabled={currentPage === 1}
                                                            className="flex items-center space-x-2 px-5 py-2.5 background-green-aqua text-white rounded-lg transition-all duration-200 hover:shadow-lg hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:shadow-none"
                                                        >
                                                            <IoChevronBack className="text-lg" />
                                                            <span className="font-medium">Anterior</span>
                                                        </button>
                                                        
                                                        <div className="flex items-center space-x-3">
                                                            <button
                                                                onClick={() => setCurrentPage(1)}
                                                                disabled={currentPage === 1}
                                                                className="px-3 py-1.5 text-sm font-medium text-[#4DABA4] border-2 border-[#4DABA4] rounded-lg transition-all hover:background-green-aqua hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                                                            >
                                                                Primera
                                                            </button>
                                                            <div className="flex items-center px-4 py-2 space-x-2 bg-gray-50 rounded-lg border border-gray-200">
                                                                <input
                                                                    type="number"
                                                                    min="1"
                                                                    max={totalPages}
                                                                    value={currentPage}
                                                                    onChange={(e) => {
                                                                        const page = parseInt(e.target.value) || 1;
                                                                        setCurrentPage(Math.max(1, Math.min(totalPages, page)));
                                                                    }}
                                                                    className="w-16 px-2 py-1 text-center text-gray-800 font-semibold border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#4DABA4] focus:border-transparent"
                                                                />
                                                                <span className="text-sm font-medium text-gray-600">/ {totalPages}</span>
                                                            </div>
                                                            <button
                                                                onClick={() => setCurrentPage(totalPages)}
                                                                disabled={currentPage === totalPages}
                                                                className="px-3 py-1.5 text-sm font-medium text-[#4DABA4] border-2 border-[#4DABA4] rounded-lg transition-all hover:background-green-aqua hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                                                            >
                                                                Última
                                                            </button>
                                                        </div>
                                                        
                                                        <button
                                                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                                            disabled={currentPage === totalPages}
                                                            className="flex items-center space-x-2 px-5 py-2.5 background-green-aqua text-white rounded-lg transition-all duration-200 hover:shadow-lg hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:shadow-none"
                                                        >
                                                            <span className="font-medium">Siguiente</span>
                                                            <IoChevronForward className="text-lg" />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                            ) : (
                                                <div className="flex flex-col justify-center items-center p-20 bg-gray-100">
                                                    <IoDocumentText className="mb-4 text-6xl text-gray-400" />
                                                    <p className="font-medium text-gray-600">Documento no disponible</p>
                                                    <p className="mt-2 text-sm text-gray-500">El documento no se pudo cargar</p>
                                                </div>
                                            )
                                        ) : postContent.type === 'noticia' ? (
                                            <div className="p-6 bg-white lg:p-10">
                                                <div className="mb-8">
                                                    <div className="overflow-hidden relative w-full bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl shadow-xl aspect-video">
                                                        <img
                                                            src={postContent.thumbnail || selectedPost?.thumbnail || "/img/default.png"}
                                                            alt={postContent.title || selectedPost?.title}
                                                            className="object-cover w-full h-full transition-transform duration-300 hover:scale-105"
                                                            onError={(e) => {
                                                                e.target.src = "/img/default.png";
                                                            }}
                                                        />
                                                        <div className="absolute inset-0 bg-gradient-to-t to-transparent from-black/20"></div>
                                                    </div>
                                                </div>
                                                <div className="max-w-none">
                                                    <div className="max-w-none prose prose-lg">
                                                        <div className="text-base font-normal leading-relaxed text-gray-800 whitespace-pre-line lg:text-lg">
                                                            {stripHtml(postContent.description || selectedPost?.description || 'Sin descripción disponible')}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex justify-center items-center p-20 bg-gray-100">
                                                <p className="text-gray-500">Contenido no disponible</p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Panel Lateral - Información */}
                                    <div className="lg:col-span-1 bg-white p-6 lg:p-8 overflow-y-auto max-h-[calc(95vh-60px)] border-l-4 border-[#4DABA4] shadow-lg custom-scrollbar">
                                        <div className="mb-6">
                                            <h2 className="pb-4 mb-5 text-2xl font-extrabold leading-tight text-gray-900 border-b-2 border-gray-200 lg:text-3xl">
                                                {stripHtml(postContent.title || selectedPost?.title || 'Sin título')}
                                            </h2>
                                            
                                            {postContent.teacherName && (
                                                <div className="mb-6 p-4 bg-gradient-to-r from-[#4DABA4] to-[#3a8a84] rounded-xl shadow-md border-2 border-[#4DABA4]">
                                                    <div className="flex items-center mb-2 space-x-2">
                                                        <div className="w-2 h-2 bg-white rounded-full"></div>
                                                        <p className="text-sm font-semibold tracking-wide text-white uppercase">Profesor</p>
                                                    </div>
                                                    <p className="mb-1 text-lg font-bold text-white">
                                                        {postContent.teacherName}
                                                    </p>
                                                    {postContent.teacherRole && (
                                                        <p className="text-sm font-medium text-white text-opacity-90">
                                                            {postContent.teacherRole}
                                                        </p>
                                                    )}
                                                </div>
                                            )}

                                            {postContent.type !== 'noticia' && (
                                                <div className="p-4 mb-6 bg-gray-50 rounded-xl border border-gray-200 shadow-sm">
                                                    <h3 className="mb-3 text-sm font-bold tracking-wide text-[#4DABA4] uppercase flex items-center space-x-2">
                                                        <span className="w-1 h-4 bg-[#4DABA4] rounded"></span>
                                                        <span>Descripción</span>
                                                    </h3>
                                                    <p className="text-sm leading-relaxed text-gray-800">
                                                        {stripHtml(postContent.description || selectedPost?.description || 'Sin descripción disponible')}
                                                    </p>
                                                </div>
                                            )}

                                            {(postContent.courseId || selectedPost?.courseId || selectedPost?.CourseId) && (
                                                <div className="p-4 mb-6 bg-gradient-to-br from-[#4DABA4]/10 to-white rounded-xl border-2 border-[#4DABA4]/30 shadow-sm">
                                                    <h3 className="mb-2 text-sm font-bold tracking-wide text-[#4DABA4] uppercase">
                                                        Curso vinculado
                                                    </h3>
                                                    <p className="mb-4 text-base font-semibold text-gray-900">
                                                        {stripHtml(
                                                            postContent.courseTitle
                                                            || selectedPost?.courseTitle
                                                            || selectedPost?.CourseTitle
                                                            || 'Curso disponible'
                                                        )}
                                                    </p>
                                                    {(postContent.isEnrolledInLinkedCourse
                                                        || selectedPost?.isEnrolledInLinkedCourse) ? (
                                                        <button
                                                            type="button"
                                                            onClick={handleGoToLinkedCourse}
                                                            className="w-full px-4 py-2.5 text-sm font-semibold text-white rounded-xl transition-all duration-200 background-green-aqua hover:shadow-lg hover:scale-[1.02]"
                                                        >
                                                            Ir al curso
                                                        </button>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={handleEnrollFromPost}
                                                            disabled={enrollingFromPost}
                                                            className="w-full px-4 py-2.5 text-sm font-semibold text-white rounded-xl transition-all duration-200 background-green-aqua hover:shadow-lg hover:scale-[1.02] disabled:opacity-60"
                                                        >
                                                            {enrollingFromPost
                                                                ? 'Inscribiendo...'
                                                                : (isAuthenticated ? 'Inscribirme al curso' : 'Inicia sesión para inscribirte')}
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {/* Métricas de Engagement */}
                                        <div className="pt-6 border-t-4 border-[#4DABA4] bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 shadow-md">
                                            <h3 className="mb-4 text-sm font-bold tracking-wide text-gray-700 uppercase">Estadísticas</h3>
                                            <div className="flex flex-col space-y-4">
                                                <div className="flex justify-between items-center">
                                                    <div className="flex items-center space-x-4">
                                                        <div className="flex items-center px-4 py-3 space-x-3 bg-white rounded-xl shadow-md border-2 border-gray-200 hover:border-[#4DABA4] transition-all duration-200">
                                                            <div className="flex justify-center items-center w-8 h-8 bg-blue-100 rounded-lg">
                                                                <span className="text-xs font-bold text-blue-600">👁</span>
                                                            </div>
                                                            <div className="flex flex-col">
                                                                <span className="text-lg font-bold text-gray-900">
                                                                    {postContent.views ?? selectedPost?.views ?? 0}
                                                                </span>
                                                                <span className="text-xs font-medium text-gray-500">Vistas</span>
                                                            </div>
                                                        </div>
                                                        <button
                                                            onClick={(e) => handleToggleLike(selectedPost || postContent, e)}
                                                            className={`flex items-center px-4 py-3 space-x-3 rounded-xl shadow-md border-2 transition-colors ${
                                                                getLikeFlag(selectedPost || postContent)
                                                                    ? 'bg-gradient-to-r from-[#4DABA4] to-[#3a8a84] border-[#4DABA4]'
                                                                    : 'bg-white border-gray-200 hover:border-[#4DABA4]'
                                                            }`}
                                                        >
                                                            <IoThumbsUp className={`text-xl ${getLikeFlag(selectedPost || postContent) ? 'text-white' : 'text-[#4DABA4]'}`} />
                                                            <div className="flex flex-col">
                                                                <span className={`text-lg font-bold ${getLikeFlag(selectedPost || postContent) ? 'text-white' : 'text-gray-900'}`}>
                                                                    {selectedPost?.likes ?? postContent.likes ?? 0}
                                                                </span>
                                                                <span className={`text-xs font-medium ${getLikeFlag(selectedPost || postContent) ? 'text-white text-opacity-90' : 'text-gray-500'}`}>Likes</span>
                                                            </div>
                                                        </button>
                                                    </div>
                                                    <button
                                                        onClick={(e) => handleToggleCollection(selectedPost || postContent, e)}
                                                        className={`p-3 rounded-xl transition-all duration-200 border-2 shadow-md hover:shadow-lg ${
                                                            getCollectionFlag(selectedPost || postContent)
                                                                ? 'text-white bg-[#4DABA4] border-[#4DABA4]'
                                                                : 'text-gray-400 hover:text-white hover:bg-[#4DABA4] border-gray-200 hover:border-[#4DABA4]'
                                                        }`}
                                                        aria-label="Guardar en favoritos"
                                                    >
                                                        <SaveIcon saved={getCollectionFlag(selectedPost || postContent)} className="text-2xl" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer del Modal */}
                        <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-white border-t-2 border-gray-100">
                            <div className="flex justify-end items-center">
                                <button
                                    onClick={closePostModal}
                                    className="px-6 py-2.5 text-sm font-semibold text-white rounded-xl transition-all duration-200 background-green-aqua hover:shadow-lg hover:scale-105 active:scale-95"
                                >
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .scrollbar-hide::-webkit-scrollbar {
                    display: none;
                }
                
                .custom-scrollbar::-webkit-scrollbar {
                    width: 8px;
                }
                
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: #f1f1f1;
                    border-radius: 10px;
                }
                
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #4DABA4;
                    border-radius: 10px;
                }
                
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: #3a8a84;
                }
                
                @keyframes fadeIn {
                    from {
                        opacity: 0;
                    }
                    to {
                        opacity: 1;
                    }
                }
                
                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                .animate-fadeIn {
                    animation: fadeIn 0.2s ease-out;
                }
                
                .animate-slideUp {
                    animation: slideUp 0.3s ease-out;
                }
            `}</style>

            {enrollAlert.open && (
                <div className="fixed bottom-6 left-1/2 z-[9999] px-4 w-full max-w-lg -translate-x-1/2 animate-slideUp">
                    <div className="flex gap-3 items-start p-4 bg-white rounded-xl border border-[#4DABA4] shadow-2xl">
                        <div className="flex flex-shrink-0 justify-center items-center w-10 h-10 text-white rounded-full background-green-aqua">
                            ✓
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="font-semibold text-gray-900">Inscripción exitosa</p>
                            <p className="mt-1 text-sm text-gray-600">{enrollAlert.message}</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setEnrollAlert({ open: false, message: "" })}
                            className="flex-shrink-0 text-gray-400 transition-colors hover:text-gray-600"
                            aria-label="Cerrar alerta"
                        >
                            <IoClose className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Home;